import { EStudentPaymentStatus } from '@constants';
import { IUser } from '@sdk/contracts';
import { Button, Flex, Modal, Popconfirm, Switch, Tooltip, App } from 'antd';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';

import {
  ActiveWrapper,
  ButtonWrapper,
  IconWrapper,
  ImageWrapper,
  NameWrapper,
  TitleWrapper,
  Wrapper
} from './StudentActions.styles';

import HUBSPOT_ICON from '@/assets/hubspot_ucademy.svg';
import { ActionButton } from '@/components/ActionButton';
import { Icon } from '@/components/Icon';
import { useImpersonate, useEditStudentDrawer, useStudentPreview } from '@/hooks';
import {
  useMutateAcademies,
  useMutateStudents,
  useUserOnboarding,
  useMutateUserContract
} from '@/store';
import { useUserInfoByUser } from '@/store/users/user-info';

type Props = {
  student: IUser;
};

const DRAWER_Z_INDEX = 1003;

const PROPS = {
  width: '750px',
  closable: true,
  destroyOnHidden: true,
  zIndex: DRAWER_Z_INDEX,
  style: { position: 'absolute' as const, borderRadius: 8, overflow: 'hidden auto' }
};

export const StudentActions: React.FC<Props> = ({ student }) => {
  const { message } = App.useApp();
  const [modal, contextHolder] = Modal.useModal();
  const { setIsOpen: setIsEditOpen, setData } = useEditStudentDrawer();
  const { isWorking, sendNewCredentials } = useMutateAcademies();
  const { impersonateUser } = useImpersonate();
  const { setUserId } = useStudentPreview();
  const { t } = useTranslation();
  const { colors } = useTheme();
  const { toggleActivateStudent, isWorking: isMutating, removeStudent } = useMutateStudents();
  const { removeUserContract } = useMutateUserContract();
  const { userOnboarding: onboarding } = useUserOnboarding({ userId: student._id });
  const { userInfo } = useUserInfoByUser(student._id);

  const handleCancel = () => setUserId();

  const { name, email, _id, isActive, hubspotId } = student;
  const contract = onboarding?.contract;
  const paymentStatusInfo = userInfo?.paymentStatusInfo;

  const isStudentDisabled = useMemo(
    () =>
      paymentStatusInfo?.currentStatus === EStudentPaymentStatus.DOWNTURN ||
      paymentStatusInfo?.currentStatus === EStudentPaymentStatus.NON_PAYMENT,
    [paymentStatusInfo]
  );

  const handleEditStudent = () => {
    setData(student);
    setIsEditOpen(true, PROPS);
  };

  const handleResetPassword = async () => {
    modal.confirm({
      title: t('ARE_YOU_SURE_TO_RESET_PASSWORD'),
      onOk: () => sendNewCredentials(_id)
    });
  };

  const handleImpersonate = () => {
    modal.confirm({
      title: t('ARE_YOU_SURE_TO_IMPERSONATE', { name }),
      onOk: () => impersonateUser(email)
    });
  };

  const handleGoToHubspot = () => {
    if (!hubspotId) return message.error(t('HUBSPOT_CONTACT_NOT_FOUND'));

    return window.open(
      `https://app.hubspot.com/contacts/${
        import.meta.env['VITE_HUBSPOT_ACCOUNT_ID']
      }/contact/${hubspotId}`,
      '_blank'
    );
  };
  const handleRemoveUser = async () => {
    modal.confirm({
      title: t('ARE_YOU_SURE_TO_DELETE_USER'),
      onOk: () => removeStudent(_id, { onSuccess: () => handleCancel() })
    });
  };

  return (
    <Wrapper vertical justify="space-between">
      {contextHolder}
      <Flex vertical gap={24}>
        <TitleWrapper>{t('ACTIONS')}</TitleWrapper>
        <ActionButton name={t('EDIT_STUDENT')} icon="Edit" onClick={handleEditStudent} />
        <ActionButton
          name={t('RESEND_KEYS')}
          icon="Password"
          loading={isWorking}
          onClick={handleResetPassword}
        />
        <ActionButton name={t('IMPERSONATE_STUDENT')} icon="Group" onClick={handleImpersonate} />
        <ButtonWrapper onClick={handleGoToHubspot}>
          <IconWrapper>
            <ImageWrapper src={HUBSPOT_ICON} alt="Icon" color="white"></ImageWrapper>
          </IconWrapper>
          <Button type="text" loading={isWorking}>
            <NameWrapper>{t('GO_TO_HUBSPOT_CONTACT')}</NameWrapper>
          </Button>
        </ButtonWrapper>
        <Popconfirm
          title={t('REMOVE_CONTRACT_CONFIRMATION')}
          styles={{ body: { maxWidth: '400px' } }}
          onConfirm={() => removeUserContract(_id)}>
          <div>
            <ActionButton disabled={!contract} name={t('REMOVE_CONTRACT')} icon="DocumentSigned" />
          </div>
        </Popconfirm>
        <Flex gap={8}>
          <Tooltip placement="top" title={isStudentDisabled ? t('STUDENT_DISABLED_TOOLTIP') : ''}>
            <Switch
              disabled={isStudentDisabled}
              loading={isMutating}
              checked={isActive}
              style={{ backgroundColor: isActive ? colors.success : colors.red6 }}
              onChange={() => toggleActivateStudent(_id)}
            />
            <ActiveWrapper>{isActive ? t('ACTIVE_ACCOUNT') : t('INACTIVE_ACCOUNT')}</ActiveWrapper>
          </Tooltip>
        </Flex>
      </Flex>
      <Flex>
        <Button
          danger
          type="link"
          loading={isMutating}
          icon={<Icon i="TrashCan" />}
          onClick={handleRemoveUser}>
          {t('REMOVE_STUDENT')}
        </Button>
      </Flex>
    </Wrapper>
  );
};
